import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { HOMEPAGE } from "../../home/routes";
import { nr1HubFromToken } from "../nr1Hub";
import { NR1_DASHBOARD_PAGE, NR1_PUBLIC_FORM_PAGE } from "../routes";

import { getMassBalanceStoresApi, type StoreShort } from "../../estoque/api/massBalanceApi";
import { listEvaluationFormsApi, type EvaluationForm } from "../../evaluations/evaluationFormsApi";
import {
  createNr1LinkBatchApi,
  listNr1LinkBatchesApi,
  revokeNr1LinkBatchApi,
  type Nr1LinkBatch,
} from "../nr1Api";

import "./Nr1Pages.css";

const STATUS_LABEL: Record<Nr1LinkBatch["status"], string> = {
  ACTIVE: "Ativo",
  EXPIRED: "Expirado",
  REVOKED: "Revogado",
};

const STATUS_PILL: Record<Nr1LinkBatch["status"], string> = {
  ACTIVE: "ecoPill",
  EXPIRED: "ecoPill nr1Pill--warn",
  REVOKED: "ecoPill ecoPill--off",
};

type BatchFilter = "active" | "all";

const BATCH_FILTERS: { value: BatchFilter; label: string }[] = [
  { value: "active", label: "Ativos" },
  { value: "all", label: "Todos" },
];

/** Quantos links aparecem na caixa antes do "e mais N". */
const LINKS_PREVIEW = 4;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });

const fmtShortDate = (iso: string) => new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

const Nr1LinksAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const hub = nr1HubFromToken(token);
  const goBack = useGoBack(hub.to);

  const [stores, setStores] = useState<StoreShort[]>([]);
  const [forms, setForms] = useState<EvaluationForm[]>([]);
  const [batches, setBatches] = useState<Nr1LinkBatch[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedFormId, setSelectedFormId] = useState("");
  const [quantity, setQuantity] = useState<number>(30);
  const [expiresAtDate, setExpiresAtDate] = useState<string>("");

  const [filterStoreId, setFilterStoreId] = useState("");
  const [batchFilter, setBatchFilter] = useState<BatchFilter>("active");
  const [revokeTarget, setRevokeTarget] = useState<Nr1LinkBatch | null>(null);

  // Links do último lote gerado (tokens em claro só existem aqui)
  const [generatedLinks, setGeneratedLinks] = useState<string[]>([]);
  const [generatedStoreName, setGeneratedStoreName] = useState("");

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  const loadBatches = useCallback(async (storeId?: string) => {
    try {
      const data = await listNr1LinkBatchesApi(storeId || undefined);
      setBatches(data);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao listar lotes.");
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [storesData, formsData] = await Promise.all([
          getMassBalanceStoresApi(token),
          listEvaluationFormsApi({}),
        ]);
        setStores(storesData || []);
        setForms((formsData || []).filter((f) => f.category === "NR1"));
      } catch {
        toast.error("Erro ao carregar lojas/formulários.");
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadBatches(filterStoreId || undefined);
  }, [filterStoreId, token, loadBatches]);

  const canGenerate = useMemo(() => {
    return (
      !!selectedStoreId &&
      !!selectedFormId &&
      quantity >= 1 &&
      quantity <= 500 &&
      !!expiresAtDate &&
      !loading
    );
  }, [selectedStoreId, selectedFormId, quantity, expiresAtDate, loading]);

  const buildLinkUrl = (t: string) => `${window.location.origin}${NR1_PUBLIC_FORM_PAGE(t)}`;

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canGenerate) return;

    const expiresAt = new Date(`${expiresAtDate}T23:59:59`);
    if (Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()) {
      toast.error("A data de expiração deve ser futura.");
      return;
    }

    try {
      setLoading(true);
      const result = await createNr1LinkBatchApi({
        storeId: selectedStoreId,
        formId: selectedFormId,
        quantity,
        expiresAt: expiresAt.toISOString(),
      });

      const storeName = stores.find((s) => s.id === selectedStoreId)?.nomeFantasia ?? "";
      setGeneratedLinks(result.tokens.map(buildLinkUrl));
      setGeneratedStoreName(storeName);

      toast.success(`${result.tokens.length} links gerados com sucesso!`);
      await loadBatches(filterStoreId || undefined);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao gerar links.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(generatedLinks.join("\n"));
      toast.success("Links copiados para a área de transferência!");
    } catch {
      toast.error("Não foi possível copiar automaticamente. Use o download CSV.");
    }
  };

  const handleDownloadCsv = () => {
    const header = "link\n";
    const csv = header + generatedLinks.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nr1-links-${generatedStoreName || "loja"}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setLoading(true);
      await revokeNr1LinkBatchApi(revokeTarget.id);
      toast.success("Lote revogado.");
      setRevokeTarget(null);
      await loadBatches(filterStoreId || undefined);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao revogar lote.");
    } finally {
      setLoading(false);
    }
  };

  const visibleBatches = useMemo(
    () => (batchFilter === "active" ? batches.filter((b) => b.status === "ACTIVE") : batches),
    [batches, batchFilter],
  );

  if (!token) return null;

  const renderAdhesion = (b: Nr1LinkBatch) => {
    const rate = b.totalLinks > 0 ? b.usedLinks / b.totalLinks : 0;
    return (
      <span className="nr1Bar">
        <span className="nr1Bar__track" aria-hidden="true">
          <span className="nr1Bar__fill" style={{ width: `${Math.round(rate * 100)}%` }} />
        </span>
        {b.usedLinks === 0 && b.status === "ACTIVE" ? (
          <span className="nr1Bar__note">novo</span>
        ) : (
          <strong>{Math.round(rate * 100)}%</strong>
        )}
      </span>
    );
  };

  return (
    <div className="nr1Page">
      <main className="nr1Page__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: hub.label, onClick: () => navigate(hub.to) },
            { label: "NR1, links anônimos" },
          ]}
          title="Links anônimos da pesquisa NR1"
          subtitle="Gere um link de uso único por funcionário e acompanhe a adesão de cada loja sem identificar quem respondeu."
          onBack={goBack}
          backLabel={`Voltar para ${hub.label}`}
          actions={
            <button type="button" className="ecoBtn" onClick={() => navigate(NR1_DASHBOARD_PAGE())}>
              <EcoIcon name="pieChart" size={18} />
              Ver riscos por loja
            </button>
          }
        />

        {/* Geração de lote */}
        <section className="ecoCard nr1Panel" aria-labelledby="nr1-gerar">
          <div>
            <h2 id="nr1-gerar" className="ecoCard__title">Gerar novo lote de links</h2>
            <p className="ecoCard__subtitle">
              Um link por funcionário. Cada link funciona uma única vez e expira na data escolhida.
            </p>
          </div>

          <form className="nr1Gen" onSubmit={handleGenerate}>
            <div className="ecoField nr1Gen__store">
              <label htmlFor="g-loja">Loja</label>
              <select id="g-loja" className="ecoSelect" value={selectedStoreId} onChange={(e) => setSelectedStoreId(e.target.value)}>
                <option value="">Selecione a loja</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.nomeFantasia}</option>
                ))}
              </select>
            </div>

            <div className="ecoField nr1Gen__form">
              <label htmlFor="g-form">Formulário</label>
              <select id="g-form" className="ecoSelect" value={selectedFormId} onChange={(e) => setSelectedFormId(e.target.value)}>
                <option value="">Selecione o formulário</option>
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>{f.title}</option>
                ))}
              </select>
            </div>

            <div className="ecoField">
              <label htmlFor="g-qtd">Quantidade de links</label>
              <input
                id="g-qtd"
                className="ecoInput"
                type="number"
                min={1}
                max={500}
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
              />
            </div>

            <div className="ecoField">
              <label htmlFor="g-exp">Expira em</label>
              <input
                id="g-exp"
                className="ecoInput"
                type="date"
                value={expiresAtDate}
                onChange={(e) => setExpiresAtDate(e.target.value)}
              />
            </div>

            <button type="submit" className="ecoBtn ecoBtn--primary nr1Gen__submit" disabled={!canGenerate}>
              <EcoIcon name="link" size={18} />
              {loading ? "Gerando..." : "Gerar links"}
            </button>
          </form>

          {generatedLinks.length > 0 ? (
            <div className="nr1Generated" role="status">
              <div className="nr1Generated__head">
                <EcoIcon name="alertTriangle" size={20} className="nr1Generated__icon" />
                <div className="nr1Generated__text">
                  <strong>
                    {generatedLinks.length} links gerados{generatedStoreName ? ` para ${generatedStoreName}` : ""}. Copie
                    ou baixe agora.
                  </strong>
                  <span>Por segurança, eles não podem ser exibidos de novo depois que esta caixa for fechada.</span>
                </div>
              </div>

              <div className="nr1Generated__actions">
                <button type="button" className="ecoBtn ecoBtn--primary" onClick={handleCopyAll}>
                  <EcoIcon name="copy" size={18} />
                  Copiar os {generatedLinks.length} links
                </button>
                <button type="button" className="ecoBtn nr1Generated__ghost" onClick={handleDownloadCsv}>
                  <EcoIcon name="download" size={18} />
                  Baixar CSV
                </button>
                <button type="button" className="ecoBtn nr1Generated__ghost" onClick={() => setGeneratedLinks([])}>
                  Já copiei, fechar
                </button>
              </div>

              <div className="nr1Generated__list">
                {generatedLinks.slice(0, LINKS_PREVIEW).map((link) => (
                  <span key={link}>{link}</span>
                ))}
                {generatedLinks.length > LINKS_PREVIEW ? (
                  <span className="nr1Generated__more">e mais {generatedLinks.length - LINKS_PREVIEW} links</span>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>

        {/* Lotes existentes */}
        <section className="ecoCard nr1Panel nr1Panel--flush" aria-labelledby="nr1-lotes">
          <div className="nr1Panel__head">
            <div>
              <h2 id="nr1-lotes" className="ecoCard__title">Lotes gerados</h2>
              <p className="ecoCard__subtitle">Adesão é a parte dos links de cada lote que já foi respondida.</p>
            </div>
            <div className="nr1Panel__tools">
              <label htmlFor="l-loja" className="ecoVisuallyHidden">Filtrar por loja</label>
              <select
                id="l-loja"
                className="ecoSelect ecoSelect--sm nr1Panel__storeFilter"
                value={filterStoreId}
                onChange={(e) => setFilterStoreId(e.target.value)}
              >
                <option value="">Todas as lojas</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.nomeFantasia}</option>
                ))}
              </select>
              <EcoSegmented label="Status" options={BATCH_FILTERS} value={batchFilter} onChange={setBatchFilter} />
            </div>
          </div>

          {visibleBatches.length === 0 ? (
            <div className="nr1Panel__pad">
              <p className="ecoEmpty">
                {batches.length === 0 ? "Nenhum lote gerado ainda." : "Nenhum lote ativo. Escolha \"Todos\" para ver os expirados e revogados."}
              </p>
            </div>
          ) : (
            <>
              <div className="ecoTableWrap nr1Desktop">
                <table className="ecoTable nr1Table">
                  <thead>
                    <tr>
                      <th scope="col">Loja</th>
                      <th scope="col">Formulário</th>
                      <th scope="col">Criado</th>
                      <th scope="col">Expira</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="ecoTable__num">Respondidos</th>
                      <th scope="col">Adesão</th>
                      <th scope="col" className="ecoTable__num">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleBatches.map((b) => (
                      <tr key={b.id}>
                        <td className="nr1Table__strong">{b.store.nomeFantasia}</td>
                        <td className="ecoTable__muted">{b.form.title}</td>
                        <td className="ecoTable__muted">{fmtDate(b.createdAt)}</td>
                        <td className="ecoTable__muted">{fmtDate(b.expiresAt)}</td>
                        <td><span className={STATUS_PILL[b.status]}>{STATUS_LABEL[b.status]}</span></td>
                        <td className="ecoTable__num ecoTable__muted">{b.usedLinks} de {b.totalLinks}</td>
                        <td>{renderAdhesion(b)}</td>
                        <td className="ecoTable__num">
                          {b.status === "ACTIVE" ? (
                            <button
                              type="button"
                              className="ecoBtn ecoBtn--sm ecoBtn--danger"
                              onClick={() => setRevokeTarget(b)}
                              disabled={loading}
                              aria-label={`Revogar lote de ${b.store.nomeFantasia}`}
                            >
                              Revogar
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="nr1Cards nr1Mobile">
                {visibleBatches.map((b) => (
                  <li key={b.id} className="nr1Card">
                    <div className="nr1Card__top">
                      <div className="nr1Card__text">
                        <span className="nr1Card__title">{b.store.nomeFantasia}</span>
                        <span className="nr1Card__meta">
                          {b.form.title} · {b.status === "EXPIRED" ? "expirou" : "expira"} {fmtShortDate(b.expiresAt)}
                        </span>
                      </div>
                      <span className={STATUS_PILL[b.status]}>{STATUS_LABEL[b.status]}</span>
                    </div>
                    <div className="nr1Card__adhesion">
                      {renderAdhesion(b)}
                      <span className="nr1Card__meta">{b.usedLinks} de {b.totalLinks}</span>
                    </div>
                    {b.status === "ACTIVE" ? (
                      <button
                        type="button"
                        className="ecoBtn ecoBtn--sm ecoBtn--danger nr1Card__action"
                        onClick={() => setRevokeTarget(b)}
                        disabled={loading}
                      >
                        Revogar lote
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </main>

      <EcoConfirmDialog
        open={Boolean(revokeTarget)}
        title="Revogar lote?"
        message={
          revokeTarget ? (
            <>
              O lote de <strong>{revokeTarget.totalLinks} links</strong> da loja{" "}
              <strong>{revokeTarget.store.nomeFantasia}</strong> será revogado. Os links pendentes deixarão de
              funcionar.
            </>
          ) : null
        }
        confirmLabel="Revogar"
        tone="danger"
        busy={loading}
        onConfirm={handleRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
};

export default Nr1LinksAdminPage;
